import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import BackButton from '../../shared/components/back-button/BackButton';
import useCompanyLocations from '../hooks/useCompanyLocations';
import {
  createCompanyLocation,
  updateCompanyLocation,
} from '../locationRepository';
import { assignNewLocationToAllEntities } from '../migrateDefaultLocation';
import LocationEditorForm from './LocationEditorForm';
import useLocationsSettingsTheme from './useLocationsSettingsTheme';
import {
  SETTINGS_LOCATIONS_PATH,
  locationsSettingsNavigationState,
  buildLocationEditPath,
} from './locationSettingsPaths';
import '../../components/settings/pages/BusinessInformation/BusinessInformation.css';
import './LocationsSettings.css';

const LocationEditPage = ({ embedded = false }) => {
  const { locationId } = useParams();
  const isCreate = !locationId;
  const navigate = useNavigate();
  const cssVars = useLocationsSettingsTheme();
  const { companyData } = useAuth();
  const { setNotification } = useNotification();
  const { locations, loading, reload } = useCompanyLocations({
    companyId: companyData?.id,
    companyData,
    autoMigrate: true,
  });
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(isCreate);

  const location = useMemo(
    () => (locationId ? locations.find((entry) => entry.id === locationId) : null),
    [locations, locationId]
  );

  useEffect(() => {
    if (isCreate) {
      setReady(true);
      return;
    }
    if (!loading) {
      setReady(true);
    }
  }, [isCreate, loading]);

  const backToLocations = () => {
    navigate(SETTINGS_LOCATIONS_PATH, { state: locationsSettingsNavigationState() });
  };

  const handleSave = async (formData) => {
    if (!companyData?.id) return;

    setSaving(true);
    try {
      if (isCreate) {
        const created = await createCompanyLocation(companyData.id, {
          ...formData,
          businessHours: formData.useCompanyHours ? null : formData.businessHours,
          openOnHolidays: formData.useCompanyHours ? null : formData.openOnHolidays,
        });

        await assignNewLocationToAllEntities({
          companyId: companyData.id,
          locationId: created.id,
          ownerId: companyData.ownerId || companyData.createdBy,
        });

        setNotification({
          open: true,
          message: 'Location created.',
          severity: 'success',
        });
      } else {
        await updateCompanyLocation(companyData.id, locationId, {
          name: formData.name,
          phone: formData.phone,
          timezone: formData.timezone,
          active: formData.active,
          isDefault: formData.isDefault,
          address: formData.address,
          useCompanyHours: formData.useCompanyHours,
          businessHours: formData.useCompanyHours ? null : formData.businessHours,
          openOnHolidays: formData.useCompanyHours ? null : formData.openOnHolidays,
        });

        setNotification({
          open: true,
          message: 'Location updated.',
          severity: 'success',
        });
      }

      await reload();
      backToLocations();
    } catch (error) {
      console.error('Failed to save location', error);
      setNotification({
        open: true,
        message: 'Failed to save location.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!companyData?.id) {
    return <Navigate to={SETTINGS_LOCATIONS_PATH} replace />;
  }

  if (!isCreate && ready && !loading && !location) {
    return <Navigate to={SETTINGS_LOCATIONS_PATH} replace state={locationsSettingsNavigationState()} />;
  }

  const pageContent = (
    <>
      <div className="locations-settings__page-topbar">
        <BackButton
          label="Back to locations"
          className="back-btn--flush"
          onClick={backToLocations}
        />
      </div>

      <div className="businessInfoHeader locations-settings__editor-header">
        <div>
          <h1>{isCreate ? 'Add location' : `Edit ${location?.name || 'location'}`}</h1>
          <p>
            {isCreate
              ? 'Add another site with its own address, timezone, and optional custom hours.'
              : 'Update address, timezone, hours, and booking visibility for this site.'}
          </p>
        </div>
      </div>

      {!ready || loading ? (
        <p className="biHelperText">Loading location…</p>
      ) : (
        <section className={`biCard locations-settings__editor-card${embedded ? ' locations-settings__editor-card--embedded' : ''}`}>
          <div className="biCardBody">
            <LocationEditorForm
              location={isCreate ? null : location}
              companyData={companyData}
              saving={saving}
              onCancel={backToLocations}
              onSave={handleSave}
            />
          </div>
        </section>
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="businessInfoPage locations-settings locations-settings--embedded" style={cssVars}>
        <div className="businessInfoInner">
          {pageContent}
        </div>
      </div>
    );
  }

  return (
    <div className="businessInfoPage locations-settings locations-settings--editor" style={cssVars}>
      <div className="businessInfoInner">
        {pageContent}
      </div>
    </div>
  );
};

export default LocationEditPage;

export function LegacyLocationEditRedirect() {
  const { locationId } = useParams();
  return <Navigate to={buildLocationEditPath(locationId)} replace />;
}
